/* eslint-disable no-nested-ternary */
import { useNavigationStore } from 'store';
import { Stack, Typography, ClickAwayListener } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { NavigationBackground } from './NavigationBackground';
import { NavigationHeader } from './NavigationHeader';
import { NavigationSearchBar } from './search-bar/NavigationSearchBar';
import { NavigationCategories } from './NavigationCategories';
import { getNavigationData } from '@/api/queries';
import { TourType } from '@/_models/Tour';
import { getQueryVariable, getVersion } from '../util';
import { randomNumberId } from '@/utils/randomNumberId';
import { TeleportOverlay } from './TeleportOverlay';
import { NavigationIntroScreen } from '@/components/navigation/NavigationIntroScreen/NavigationIntroScreen';
import { NavigationStation, NavigationWrapperProps } from '@/components/navigation/types';
import DeviceManager from '../util/deviceManager';
import { DetailedRoutesMobile } from './DetailedRoutesMobile';
import { useStyles } from './styles';
import { useDetailedRoutes } from './hooks/useDetailedRoutes';
import { NavigationType } from './NavigationIntroScreen/components/Popup/NavigationIntroChoicePopup';
import { ZoomableMap } from './ZoomableMap';

export enum TeleportGroupType {
  Elevator = 'elevator',
  Stairway = 'stairway',
}

export const NavigationWrapper = ({
  isBackgroundVisible,
  setBackgroundVisible,
  setDestination,
  actionPathFinished,
  tour,
  targetLocation,
  moveToStartingPoint,
  tourType,
  appState,
  setActionPathFinished,
  pathFinderLine,
  rtl,
  showTeleportLoading,
  setShowTeleportLoading,
  endTeleportName,
  translation,
  endTpType,
  setDescriptionEnabled,
  allSweeps,
  currentSweep,
  THREE,
  setActionPathNavbarPlayButtonType,
  showcaseLoaded,
  allTags,
  teleportPoints: teleportPointsProp,
  showActionPathBar,
  navigationStartPosition,
  resetNavigationByCloseButton,
  shouldNavReset,
  showCaseTemp,
  setIsTpUp,
  isTpUp,
  setPathResult,
}: NavigationWrapperProps) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [navigationItems, setNavigationItems] = useState([]);
  const [navigationCategories, setNavigationCategories] = useState([]);
  const navigationSettings = useNavigationStore((state) => state.navigationSettings);
  const setNavigationSettings = useNavigationStore((state) => state.setNavigationSettings);
  const navigationFinishScreen = useNavigationStore((state) => state.navigationFinishScreen);
  const setNavigationFinishScreen = useNavigationStore((state) => state.setNavigationFinishScreen);
  const setNavigationTranslation = useNavigationStore((state) => state.setNavigationTranslation);
  const [navigationStations, setNavigationStations] = useState<NavigationStation[]>([]);
  const { filtersEnabled, sortingEnabled, defaultSorting } = navigationSettings;
  const [isSearchBarFocused, setIsSearchBarFocused] = useState(false);
  const [isTagsOverlayHidden, setIsTagsOverlayHidden] = useState(false);
  const [hiddenFromLink, setHiddenFromLink] = useState(false);
  const [kioskIsReset, setKioskIsReset] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showLoader, setShowLoader] = useState(true);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const [mobilePosition, setMobilePosition] = useState(1);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupHeight, setPopupHeight] = useState(0);
  const [startingPoint, setStartingPoint] = useState(null);
  const [navigationSharingPopupOpen, setNavigationSharingPopupOpen] = useState(false);
  const [choicePopupOpen, setChoicePopupOpen] = useState(false);
  const [sharePopupOpen, setSharePopupOpen] = useState(false);
  const [showDetailedRoutesOnly, setShowDetailedRoutesOnly] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const appStateRef = useRef(appState);
  const [navType, setNavType] = useState(NavigationType.Shortest);
  const choicePopupSearchOpen = useNavigationStore((state) => state.showChoicePopupSearchField);

  const userLogo = tour?.user?.whitelabelId
    ? `${process.env.NEXT_PUBLIC_CDN}/users/${tour.user.id}/logo.png${getVersion(
        tour.user.updatedAt,
      )}`
    : '/images/logo-black.svg';
  const logo = tour.client?.logo
    ? `${process.env.NEXT_PUBLIC_CDN}/clients/${tour.client?.id}/logo.png${getVersion(
        tour.client?.updatedAt,
      )}`
    : userLogo;
  const mapEnabled =
    tour.slug === '78911a0a' &&
    isBackgroundVisible &&
    tourType === TourType.Kiosk &&
    startingPoint?.station?.title === 'Sourasky entrance (Piano)';

  useEffect(() => {
    setNavigationTranslation(translation);
  }, [setNavigationTranslation, translation]);

  useEffect(() => {
    async function fetchData() {
      // You can await here
      const {
        navigationItems: fetchedItems,
        navigationCategories: fetchedCategories,
        navigationFinishScreen: fetchedFinishScreen,
        navigationSettings: fetchedSettings,
        navigationStations: fetchedStations,
      } = await getNavigationData(tour.id);

      const otherCategoryId = randomNumberId();

      const itemsWithoutCategory = fetchedItems.filter(
        (item: any) => !item.category || !item.categoryId,
      );
      setNavigationItems(
        fetchedItems.map((item) => ({ ...item, categoryId: item.categoryId || otherCategoryId })),
      );
      setNavigationCategories([
        ...fetchedCategories.filter((category) => category.items.length),
        {
          id: otherCategoryId,
          title: translation?.navOther || 'Other',
          items: itemsWithoutCategory,
          isOther: true,
        },
      ]);
      if (fetchedSettings) setNavigationSettings(fetchedSettings);
      if (fetchedFinishScreen) setNavigationFinishScreen(fetchedFinishScreen);
      if (fetchedStations) setNavigationStations(fetchedStations);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour.id, translation]);

  const selectCategory = (category: any) => {
    setShowSkeleton(true);
    setSelectedCategory(category);
    setTimeout(() => {
      setShowSkeleton(false);
    }, 1000);
  };

  useEffect(() => {
    const target = getQueryVariable(window.location.href, 'target');
    setHiddenFromLink(!!target);
    setPopupOpen(!!target);
  }, []);

  useEffect(() => {
    if (isBackgroundVisible && pathFinderLine) {
      pathFinderLine.inAutoNavigate = false;
    }
  }, [isBackgroundVisible, pathFinderLine]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    // Function to find and remove the tags overlay
    const findAndRemoveTagsOverlay = () => {
      const iframe = document.body.querySelector('iframe');
      const tagsNavigationOverlay =
        iframe?.contentWindow?.document.body.querySelector('.tags-overlay');
      if (tagsNavigationOverlay) {
        tagsNavigationOverlay.remove();
        setIsTagsOverlayHidden(true); // Update state to indicate the overlay is hidden
        return true; // Overlay found and removed
      }
      return false; // Overlay not found
    };

    if (!isTagsOverlayHidden) {
      const interval = setInterval(() => {
        const foundAndRemoved = findAndRemoveTagsOverlay();
        if (foundAndRemoved) {
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isTagsOverlayHidden]);

  useEffect(() => {
    appStateRef.current = appState;
  }, [appState]);

  // Function to detect swipe direction
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientY);
  };

  const handleTouchMove = (e) => {
    if (!touchStart) {
      return;
    }
    const distance = touchStart - e.targetTouches[0].clientY;
    const isSwipeDown = distance < 0;
    const isSwipeUp = distance > 0;

    if (isSwipeDown) {
      if (showDetailedRoutesOnly) {
        setMobilePosition(1);
        setBackgroundVisible(false);
        setShowDetailedRoutesOnly(false);
        setTouchStart(null);

        return;
      }
      if (
        ((choicePopupOpen && choicePopupSearchOpen) ||
          (!popupOpen &&
            !choicePopupOpen &&
            ((selectedCategory && !selectedItem) || !!searchText))) &&
        mobilePosition > 1
      ) {
        setTouchStart(null);
        return;
      }
      // Handle swipe down
      setMobilePosition((prev) => (prev > 0 ? prev - 1 : prev));
    }
    if (isSwipeUp) {
      // Handle swipe up
      setMobilePosition((prev) => (prev < 2 ? prev + 1 : prev));
    }
    setTouchStart(null);
  };

  useEffect(() => {
    if (selectedCategory && DeviceManager.isMobile()) {
      setMobilePosition(2);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (allSweeps && currentSweep) {
      if (tourType === TourType.Regular) {
        const foundSweep = Array.from(allSweeps.values()).find(
          (s: any) => s.sweep_uuid === currentSweep.uuid || s.id === currentSweep.id,
        );
        if (foundSweep) setStartingPoint(foundSweep);
      }
      if (tourType === TourType.Kiosk && navigationStartPosition) {
        const { s, x, y } = navigationStartPosition;

        const foundStation = navigationStations?.find((station) => {
          const stationS = getQueryVariable(station.url, 's');
          const stationX = getQueryVariable(station.url, 'x');
          const stationY = getQueryVariable(station.url, 'y');
          return stationS === s && stationX === x && stationY === y;
        });
        if (foundStation) {
          const foundSweep = Array.from(allSweeps.values()).find(
            (sweep: any) => sweep.sweep_uuid === s || sweep.id === s,
          );
          if (foundSweep)
            setStartingPoint({ ...(foundSweep as Record<string, unknown>), station: foundStation });
        }
      }
    }
  }, [allSweeps, allTags, currentSweep, tourType, navigationStartPosition, navigationStations]);

  const { detailedRoutes, resetDetailedRoutes } = useDetailedRoutes({
    pathFinderLine,
    navigationSettings,
    targetLocation,
    startingPoint,
    allSweeps,
    THREE,
    navType,
  });

  const openDetailedRoutes = () => {
    setBackgroundVisible(true);
    setShowDetailedRoutesOnly(true);
    setMobilePosition(2);
  };

  const closeDetailedRoutesList = () => {
    setMobilePosition(1);
    setBackgroundVisible(false);

    setTimeout(() => {
      setShowDetailedRoutesOnly(false);
    }, 300);
  };

  const { presentationStyle, mainWrapperStyle, whiteLineStyle, whiteLineWrapperStyle } = useStyles({
    isBackgroundVisible,
    navigationSharingPopupOpen,
    actionPathFinished,
    choicePopupOpen,
    mobilePosition,
    popupHeight,
    navigationSettings,
    popupOpen,
    sharePopupOpen,
    selectedCategory,
  });

  return (
    <>
      {showTeleportLoading && (
        <TeleportOverlay
          rtl={rtl}
          setShowTeleportLoading={setShowTeleportLoading}
          endTeleportName={endTeleportName}
          endTpType={endTpType}
          isTpUp={isTpUp}
        />
      )}
      {showLoader && (
        <NavigationIntroScreen
          logo={logo}
          appState={appState}
          tour={tour}
          rtl={rtl}
          onLoaded={() => {
            setShowLoader(false);
            if (appStateRef.current === 'playing') {
              setTimeout(() => {
                setShowSkeleton(false);
              }, 2000);
            } else {
              setTimeout(() => {
                setShowSkeleton(false);
              }, 2000);
            }
          }}
        />
      )}

      {!mapEnabled && (
        <NavigationBackground
          showcaseLoaded={showcaseLoaded}
          isBackgroundVisible={isBackgroundVisible}
          tour={tour}
        />
      )}
      {!showLoader && (
        <>
          {showActionPathBar &&
            navigationSettings?.detailedRouteEnabled &&
            DeviceManager.isMobile() &&
            !actionPathFinished &&
            detailedRoutes?.length > 0 && (
              <DetailedRoutesMobile
                currentSweep={currentSweep}
                detailedRoutes={detailedRoutes}
                openDetailedRoutes={openDetailedRoutes}
                isBackgroundVisible={isBackgroundVisible}
                rtl={rtl}
              />
            )}

          <Stack sx={mainWrapperStyle}>
            {!navigationSharingPopupOpen && (
              <NavigationHeader
                rtl={rtl}
                isBackgroundVisible={isBackgroundVisible}
                tour={tour}
                tourType={tourType}
                mobilePosition={mobilePosition}
              />
            )}

            <ClickAwayListener
              onClickAway={() => {
                setIsSearchBarFocused(false);
                if (!mapEnabled) setSelectedCategory(null);
              }}
            >
              <Stack role="presentation" sx={presentationStyle}>
                {!(navigationSharingPopupOpen && actionPathFinished) && DeviceManager.isMobile() && (
                  <Stack
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    sx={whiteLineWrapperStyle}
                  >
                    <Stack id="white-line" sx={whiteLineStyle} />
                  </Stack>
                )}
                <Stack
                  alignItems="center"
                  justifyContent="flex-start"
                  width={{ xs: '100%', overflow: 'hidden' }}
                  sx={{
                    gap: '10px',
                  }}
                >
                  <NavigationSearchBar
                    appState={appState}
                    isBackgroundVisible={isBackgroundVisible}
                    menuItems={navigationItems}
                    setDestination={setDestination}
                    setBackgroundVisible={(val: boolean) => setBackgroundVisible(val)}
                    actionPathFinished={actionPathFinished}
                    selectedCategory={selectedCategory}
                    selectedItem={selectedItem}
                    setSelectedItem={setSelectedItem}
                    tour={tour}
                    target={targetLocation}
                    selectCategory={selectCategory}
                    categories={navigationCategories?.filter((c) => !c.isOther)}
                    moveToStartingPoint={moveToStartingPoint}
                    filtersEnabled={filtersEnabled}
                    sortingEnabled={sortingEnabled}
                    defaultSorting={defaultSorting}
                    navigationFinishScreen={navigationFinishScreen}
                    navigationStations={navigationStations}
                    tourType={tourType}
                    setActionPathFinished={setActionPathFinished}
                    isSearchBarFocused={isSearchBarFocused}
                    setIsSearchBarFocused={setIsSearchBarFocused}
                    rtl={rtl}
                    inAutoNavigate={pathFinderLine?.inAutoNavigate}
                    kioskIsReset={kioskIsReset}
                    setKioskIsReset={setKioskIsReset}
                    searchText={searchText}
                    setSearchText={setSearchText}
                    hiddenFromLink={hiddenFromLink}
                    setHiddenFromLink={setHiddenFromLink}
                    showSkeleton={showSkeleton}
                    setShowSkeleton={setShowSkeleton}
                    pathFinderLine={pathFinderLine}
                    setDescriptionEnabled={(val) => setDescriptionEnabled(val)}
                    mobilePosition={mobilePosition}
                    setMobilePosition={setMobilePosition}
                    popupOpen={popupOpen}
                    setPopupOpen={setPopupOpen}
                    setPopupHeight={setPopupHeight}
                    startingPoint={startingPoint}
                    onSetStartingPoint={(item) => {
                      setStartingPoint(item);
                    }}
                    allSweeps={allSweeps}
                    currentSweep={currentSweep}
                    setNavigationSharingPopupOpen={setNavigationSharingPopupOpen}
                    navigationSharingPopupOpen={navigationSharingPopupOpen}
                    detailedRoutes={detailedRoutes}
                    choicePopupOpen={choicePopupOpen}
                    setChoicePopupOpen={setChoicePopupOpen}
                    showDetailedRoutesOnly={showDetailedRoutesOnly}
                    closeDetailedRoutesList={closeDetailedRoutesList}
                    setActionPathNavbarPlayButtonType={setActionPathNavbarPlayButtonType}
                    resetDetailedRoutes={resetDetailedRoutes}
                    allTags={allTags}
                    teleportPoints={teleportPointsProp}
                    navType={navType}
                    setNavType={setNavType}
                    setStartingPoint={setStartingPoint}
                    resetNavigationByCloseButton={resetNavigationByCloseButton}
                    shouldNavReset={shouldNavReset}
                    showCaseTemp={showCaseTemp}
                    setIsTpUp={setIsTpUp}
                    setPathResult={setPathResult}
                    sharePopupOpen={sharePopupOpen}
                    setSharePopupOpen={setSharePopupOpen}
                  />
                  {isBackgroundVisible &&
                    !selectedItem &&
                    navigationCategories?.length > 1 &&
                    !selectedCategory &&
                    !searchText.length &&
                    !hiddenFromLink &&
                    !showDetailedRoutesOnly &&
                    mobilePosition > 0 && (
                      <NavigationCategories
                        rtl={rtl}
                        categories={navigationCategories}
                        handleSelectCategory={selectCategory}
                        showSkeleton={showSkeleton}
                        mobilePosition={mobilePosition}
                      />
                    )}
                </Stack>
                {isBackgroundVisible && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="start"
                    alignSelf="center"
                    sx={{
                      width: '194px',
                      gap: '7px',
                      position: 'fixed',
                      bottom: 0,
                      right: '20px',
                      mt: '4px',
                      mb: '20px',
                      display: { xs: DeviceManager.isMobile() ? 'none' : 'flex', sm: 'flex' },
                      direction: rtl ? 'rtl' : 'ltr',
                    }}
                  >
                    <Typography
                      fontSize="16px"
                      fontWeight={rtl ? 400 : 500}
                      fontStyle="normal"
                      lineHeight="28.873px"
                      sx={{ direction: rtl ? 'rtl' : 'ltr' }}
                    >
                      {translation?.navPowerBy || 'Powered by'}
                    </Typography>
                    <img
                      className="watermark"
                      width="81px"
                      alt="logo"
                      src={logo}
                      style={{ flexShrink: 0 }}
                    />
                  </Stack>
                )}
              </Stack>
            </ClickAwayListener>
            {mapEnabled && <ZoomableMap selectedItem={selectedItem} />}
          </Stack>
        </>
      )}
    </>
  );
};
